import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import AuthService from "../../services/authService";
import ProfileModal from "../ProfileModal";
import { Route as RouteType } from "../../routes";
// CSS đã được hợp nhất vào App.css

interface BottomNavigationProps {
  routes: RouteType[];
}

const BottomNavigation: React.FC<BottomNavigationProps> = ({ routes }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileWrapperRef = useRef<HTMLDivElement | null>(null);
  const userPrefix = currentUser?.role === 'staff' ? '/staff' : '/admin';

  useEffect(() => {
    const user = AuthService.getCurrentUser();
    setCurrentUser(user);
  }, []);

  // Verifies if routeName is the one active (in browser input)
  const isActiveRoute = (routeName: string): boolean => {
    return location.pathname.indexOf(routeName) > -1;
  };

  // Toggle profile modal
  const toggleProfileModal = () => {
    setProfileModalOpen(!profileModalOpen);
  };

  // Toggle profile dropdown menu
  const toggleProfileMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setProfileMenuOpen(!profileMenuOpen);
  };

  // Mở modal hồ sơ từ menu
  const openProfileFromMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setProfileMenuOpen(false);
    toggleProfileModal();
  };

  // Đóng menu hồ sơ khi click ra ngoài
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (!profileMenuOpen) return;
      const wrapper = profileWrapperRef.current;
      if (wrapper && event.target instanceof Node && !wrapper.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('touchstart', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('touchstart', handleClickOutside, true);
    };
  }, [profileMenuOpen]);

  // Đóng menu hồ sơ khi thay đổi route
  useEffect(() => {
    if (profileMenuOpen) setProfileMenuOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Xử lý khi profile được cập nhật
  const handleProfileUpdate = () => {
    // Refresh thông tin người dùng
    const user = AuthService.getCurrentUser();
    setCurrentUser(user);
  };

  // Xử lý điều hướng
  const handleNavigation = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    
    // Kiểm tra xem đường dẫn đã có prefix chưa
    if (currentUser && !path.startsWith('/auth') && !path.startsWith('/admin') && !path.startsWith('/staff')) {
      const prefix = currentUser.role === 'staff' ? '/staff' : '/admin';
      navigate(`${prefix}${path}`);
    } else {
      navigate(path);
    }
  };

  // Đăng xuất
  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setProfileMenuOpen(false);
    navigate('/auth/login');
  };

  // (Giữ điều hướng tạo đơn bằng NavLink bên dưới)

  // Lọc routes chỉ hiển thị trên mobile (ẩn Dashboard, Nhân viên, Hoạt động)
  const mobileRoutes = routes.filter(route => 
    !route.hidden && 
    (route as any).mobileVisible !== false &&
    route.name !== 'Dashboard' &&
    route.name !== 'Hoạt động' && // Ẩn Nhật ký hệ thống khỏi thanh dưới
    route.resourceType !== 'staff' // Ẩn các trang quản lý nhân viên khỏi thanh dưới
  );

  return (
    <>
      <div className="bottom-navigation d-lg-none">
        <div className="bottom-nav-container">
          {(() => {
            const firstHalfCount = Math.ceil(mobileRoutes.length / 2);
            const firstHalf = mobileRoutes.slice(0, firstHalfCount);
            const secondHalf = mobileRoutes.slice(firstHalfCount);
            return (
              <>
                {firstHalf.map((route, index) => (
                  <NavLink
                    key={`first-${index}`}
                    to={route.layout + route.path}
                    className={() => {
                      const basePath = route.layout + route.path;
                      const pathname = location.pathname;
                      // Special handling: Orders list should NOT be active on "/orders/add"
                      if (route.resourceType === 'order' && route.path === '/orders') {
                        const isListActive = pathname === basePath || (pathname.startsWith(`${route.layout}/orders/`) && !pathname.startsWith(`${route.layout}/orders/add`));
                        return `bottom-nav-item ${isListActive ? "active" : ""}`;
                      }
                      const isActive = pathname === basePath || pathname.startsWith(`${basePath}/`);
                      return `bottom-nav-item ${isActive ? "active" : ""}`;
                    }}
                  >
                    <div className="bottom-nav-icon">
                      <i className={route.icon}></i>
                    </div>
                    <span className="bottom-nav-label">{route.name}</span>
                  </NavLink>
                ))}

                {/* Nút Tạo đơn dạng item chuẩn ở giữa */}
                <NavLink
                  to={currentUser ? `${userPrefix}/orders/add` : '/auth/login'}
                  className={() => {
                    const pathname = location.pathname;
                    const addPath = `${userPrefix}/orders/add`;
                    const isActive = pathname === addPath;
                    return `bottom-nav-item ${isActive ? "active" : ""}`;
                  }}
                >
                  <div className="bottom-nav-icon">
                    <i className="fa fa-plus"></i>
                  </div>
                  <span className="bottom-nav-label">Tạo đơn</span>
                </NavLink>

                {secondHalf.map((route, index) => (
                  <NavLink
                    key={`second-${index}`}
                    to={route.layout + route.path}
                    className={() => {
                      const basePath = route.layout + route.path;
                      const pathname = location.pathname;
                      if (route.resourceType === 'order' && route.path === '/orders') {
                        const isListActive = pathname === basePath || (pathname.startsWith(`${route.layout}/orders/`) && !pathname.startsWith(`${route.layout}/orders/add`));
                        return `bottom-nav-item ${isListActive ? "active" : ""}`;
                      }
                      const isActive = pathname === basePath || pathname.startsWith(`${basePath}/`);
                      return `bottom-nav-item ${isActive ? "active" : ""}`;
                    }}
                  >
                    <div className="bottom-nav-icon">
                      <i className={route.icon}></i>
                    </div>
                    <span className="bottom-nav-label">{route.name}</span>
                  </NavLink>
                ))}
              </>
            );
          })()}

          {/* Profile button + menu */}
          <div className={`bottom-profile-wrapper`} ref={profileWrapperRef}>
            <div 
              className={`bottom-nav-item ${profileMenuOpen ? "active" : ""}`}
              onClick={toggleProfileMenu}
            >
              <div className="bottom-nav-icon">
                <i className="fa fa-user-circle"></i>
              </div>
              <span className="bottom-nav-label">Hồ sơ</span>
            </div>

            {profileMenuOpen && (
              <div className="bottom-profile-menu">
                <div className="bottom-profile-item" onClick={openProfileFromMenu}>
                  <i className="fa fa-user me-2"></i>Hồ sơ
                </div>
                {currentUser && currentUser.role === 'admin' && (
                  <div className="bottom-profile-item" onClick={(e) => handleNavigation(e, '/dashboard')}>
                    <i className="fas fa-home me-2"></i>Trang chủ
                  </div>
                )}
                {currentUser && currentUser.role === 'admin' && (
                  <div className="bottom-profile-item" onClick={(e) => handleNavigation(e, '/staff')}>
                    <i className="fa fa-users me-2"></i>Quản lý nhân viên
                  </div>
                )}
                {currentUser && (
                  <div className="bottom-profile-item" onClick={(e) => handleNavigation(e, '/logs')}>
                    <i className="fa fa-history me-2"></i>Nhật ký hệ thống
                  </div>
                )}
                <div className="bottom-profile-divider"></div>
                <div className="bottom-profile-item" onClick={handleLogout}>
                  <i className="fa fa-sign-out me-2"></i>Đăng xuất
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Modal */}
      <ProfileModal
        isOpen={profileModalOpen}
        toggle={toggleProfileModal}
        onProfileUpdate={handleProfileUpdate}
      />
    </>
  );
};

export default BottomNavigation;
